package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import kotlinx.coroutines.delay
import com.milta.core.localization.OptimizationQuotaStrings
import com.milta.feature.optimizationquota.domain.entity.OptimizationOutcome
import com.milta.feature.optimizationquota.presentation.OptimizationHistoryState
import com.milta.feature.optimizationquota.presentation.OptimizationQuotaFormat
import com.milta.feature.optimizationquota.presentation.actionLabel
import com.milta.feature.optimizationquota.presentation.fillPlaceholders
import com.milta.feature.optimizationquota.presentation.targetLabel
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.w3c.dom.HTMLElement

/** MIL-97 — building blocks of the optimization counter on the web dashboard (Enterprise + Admin). */

/** Distance to the bottom (px) at which the next history page is requested (R13). */
private const val LOAD_MORE_THRESHOLD_PX = 40

/** Lets the new rows be laid out before measuring whether the box overflows. */
private const val LAYOUT_SETTLE_MS = 50L

/** The history box's DOM element, read by the "doesn't fill the box" check. */
private class ElementHolder {
    var element: HTMLElement? = null
}

/** One line under an optimization button: the exhausted reason (retour 29/10 — never the counter). */
@Composable
internal fun QuotaHint(text: String) {
    Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "8px"); property("text-align", "center") } }) {
        Text(text)
    }
}

/** Amber banner with a bold [title] — exhausted counter (R10) or a quota reached on click (R11). */
@Composable
internal fun QuotaWarnBanner(title: String, message: String?) {
    QuotaBanner(title, message, background = WebWarningBg, ink = "#8A5A0A")
}

/** Accent banner with a bold [title] — informational (batch created without optimization). */
@Composable
internal fun QuotaInfoBanner(title: String, message: String?) {
    QuotaBanner(title, message, background = WebAccentLight, ink = WebTextPrimary)
}

@Composable
private fun QuotaBanner(title: String, message: String?, background: String, ink: String) {
    Div(
        attrs = {
            style {
                property("border-radius", "10px"); padding(11.px, 14.px)
                property("font-size", "13px"); property("line-height", "1.45"); property("margin-bottom", "12px")
                property("background", background); property("color", ink)
            }
        },
    ) {
        Div { B { Text(title) } }
        if (message != null) Div { Text(message) }
    }
}

/**
 * Paged history table (E3, A2): rows appended page by page when its scroll container reaches the
 * bottom (R13) — never the whole list. "Rendue — échec RoutaR" rows are not counted (R9).
 */
@Composable
internal fun OptimizationHistoryTable(
    history: OptimizationHistoryState,
    strings: OptimizationQuotaStrings,
    onLoadMore: () -> Unit,
    maxHeightPx: Int = 320,
) {
    // R13 safety net: when a page doesn't fill the box there is no scroll event at all — ask for
    // the next page as long as the content doesn't overflow and pages are left.
    val container = remember { ElementHolder() }
    LaunchedEffect(history.loadedPages, history.isLoadingPage, history.error) {
        delay(LAYOUT_SETTLE_MS)
        val el = container.element ?: return@LaunchedEffect
        if (history.loadedPages > 0 && history.hasMore && !history.isLoadingPage && history.error == null &&
            el.scrollHeight <= el.clientHeight
        ) {
            onLoadMore()
        }
    }
    Div(
        attrs = {
            ref { element ->
                container.element = element
                onDispose { container.element = null }
            }
            style {
                property("max-height", "${maxHeightPx}px"); property("overflow-y", "auto")
                property("border", "1px solid $WebBorder"); property("border-radius", "10px")
            }
            onScroll { event ->
                val el = event.target as? HTMLElement ?: return@onScroll
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - LOAD_MORE_THRESHOLD_PX) onLoadMore()
            }
        },
    ) {
        Table(attrs = { style { property("width", "100%"); property("border-collapse", "collapse"); property("font-size", "12.5px") } }) {
            Thead {
                Tr {
                    listOf(strings.columnDate, strings.columnAction, strings.columnTarget, strings.columnOutcome).forEach { label ->
                        Th(
                            attrs = {
                                style {
                                    property("position", "sticky"); property("top", "0"); property("background", WebSurface)
                                    property("text-align", "left"); padding(8.px, 12.px)
                                    property("font-size", "11px"); property("font-weight", "700"); property("color", WebTextSecondary)
                                    property("text-transform", "uppercase"); property("border-bottom", "1px solid $WebBorder")
                                }
                            },
                        ) { Text(label) }
                    }
                }
            }
            Tbody {
                history.items.forEach { entry ->
                    val refunded = entry.outcome == OptimizationOutcome.Refunded
                    Tr {
                        HistoryCell(OptimizationQuotaFormat.dayMonthTime(entry.occurredAt))
                        HistoryCell(strings.actionLabel(entry.action))
                        HistoryCell(strings.targetLabel(entry))
                        Td(attrs = { style { padding(8.px, 12.px); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
                            Span(
                                attrs = {
                                    style {
                                        property("padding", "3px 8px"); property("border-radius", "999px")
                                        property("font-size", "11px"); property("font-weight", "700"); property("white-space", "nowrap")
                                        property("background", if (refunded) WebSurfaceDim else WebSuccessBg)
                                        property("color", if (refunded) WebTextSecondary else WebSuccessInk)
                                    }
                                },
                            ) { Text(if (refunded) strings.outcomeRefundedLong else strings.outcomeCounted) }
                        }
                    }
                }
                Tr {
                    Td(attrs = {
                        attr("colspan", "4")
                        style { padding(10.px, 12.px); property("text-align", "center"); property("font-size", "12px"); property("color", WebTextSecondary) }
                    }) {
                        when {
                            history.isLoadingPage -> Div(attrs = { style { property("display", "inline-flex"); property("gap", "8px"); property("align-items", "center") } }) {
                                WebSpinner(size = 14, strokeWidth = 2)
                                Text(strings.historyLoadingMore)
                            }
                            history.error != null -> {
                                Span(attrs = { style { property("color", WebErrorInk); property("margin-right", "8px") } }) {
                                    Text(if (history.error == "offline") strings.errorNoConnection else strings.historyLoadError)
                                }
                                Button(attrs = { onClick { onLoadMore() }; style { secondaryButtonStyle(this) } }) { Text(strings.retryAction) }
                            }
                            history.isEmpty -> Text(strings.historyEmpty)
                            !history.hasMore -> Text(strings.historyEnd.fillPlaceholders("count" to history.items.size))
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun HistoryCell(text: String) {
    Td(attrs = { style { padding(8.px, 12.px); property("border-bottom", "1px solid $WebSurfaceDim"); property("color", WebTextPrimary) } }) {
        Text(text)
    }
}
